import { defineStore } from 'pinia';
import { ref, computed, shallowRef } from 'vue';
import { ShopifyCart, ShopifyCartItem } from '../types/servicesShopifyCart';
import type { CartStockRemoval, CartUrlNotice } from '../types/models';
import { useShopifyCartService } from '../services/shopifyCart';
import {
  freeGiftEligibleSkus,
  freeGiftProduct,
  secondFreeGiftSku,
  type FreeGiftOption,
} from '@/features/Page/data/globalData';
import { resolveRegionValue } from '@/composables/regionHelpers';

export const getFreeGiftCartSku = (option: FreeGiftOption): string =>
  option.cartSku ?? option.displaySku;

export const useCartStore = defineStore('cartStore', () => {
  const { DEFAULT_EMPTY_CART } = useShopifyCartService();
  const cartData = ref<ShopifyCart>(DEFAULT_EMPTY_CART());
  const cartDataLoaded = ref(false);
  const pendingCartMutations = ref(0);
  const cartUpdateInProgress = computed(() => pendingCartMutations.value > 0);
  const error = ref<string | null>(null);
  const cartAdjustedForStock = ref(false);
  const cartStockRemovals = ref<CartStockRemoval[]>([]);
  const cartAddLimitedByStock = ref(false);
  const cartAdjustedBeforeLatestAdd = ref(false);
  const latestAddLineIdentities = ref<string[]>([]);
  const cartUrlNotice = ref<CartUrlNotice | null>(null);
  const cartNoticesVisible = shallowRef(false);

  const chosenFreeGiftSku = shallowRef<string | null>(null);

  const resolvedFreeGiftProduct = computed(() => resolveRegionValue(freeGiftProduct));
  const freeGiftOptions = computed<FreeGiftOption[]>(
    () => resolvedFreeGiftProduct.value?.options ?? [],
  );
  const freeGiftCartSkus = computed(() => new Set(freeGiftOptions.value.map(getFreeGiftCartSku)));

  const isFreeGiftItem = (item: ShopifyCartItem) => {
    return freeGiftCartSkus.value.has(item.sku) || item.properties?._free_gift === 'true';
  };

  const isSecondFreeGiftItem = (item: ShopifyCartItem) =>
    item.properties?._second_free_gift === 'true';

  const secondFreeGiftOption = computed<FreeGiftOption | null>(() => {
    const cartSku = resolvedFreeGiftProduct.value?.secondGiftCartSku;
    if (!cartSku || !secondFreeGiftSku) return null;
    return { displaySku: secondFreeGiftSku, bundleItemSku: secondFreeGiftSku, cartSku };
  });

  const isFreeGiftSku = (sku?: string | null) => {
    return !!sku && freeGiftCartSkus.value.has(sku);
  };

  const eligibleFreeGiftSkus = computed<string[]>(() =>
    resolvedFreeGiftProduct.value ? freeGiftEligibleSkus : [],
  );

  const isFreeGiftEligibleSku = (sku?: string | null): boolean =>
    !!sku && eligibleFreeGiftSkus.value.includes(sku);

  const getFreeGiftProductButton = (sku?: string | null) => {
    const productButton = resolvedFreeGiftProduct.value?.productButton;
    if (!sku || !productButton) return null;
    return freeGiftOptions.value.some((option) => option.displaySku === sku) ? productButton : null;
  };

  const getFreeGiftOption = (item: ShopifyCartItem): FreeGiftOption | undefined => {
    const displaySku = item.properties?._free_gift_display_sku;
    if (isSecondFreeGiftItem(item)) {
      return secondFreeGiftOption.value?.displaySku === displaySku
        ? secondFreeGiftOption.value
        : undefined;
    }
    return (
      freeGiftOptions.value.find((option) => option.displaySku === displaySku) ??
      freeGiftOptions.value.find((option) => getFreeGiftCartSku(option) === item.sku)
    );
  };

  const setChosenFreeGiftSku = (displaySku: string | null) => {
    chosenFreeGiftSku.value = displaySku;
  };

  const itemCount = computed<number>(() => cartData.value.item_count);
  const totalPrice = computed<number>(() => cartData.value.total_price);
  const items = computed<ShopifyCartItem[]>(() => cartData.value.items);
  const isEmpty = computed<boolean>(() => itemCount.value === 0);
  const subtotal = computed<number>(() => cartData.value.items_subtotal_price);
  const totalDiscount = computed<number>(() => cartData.value.total_discount);

  const MAX_QUANTITY = 5;

  const startCartMutation = () => {
    pendingCartMutations.value += 1;
  };

  const finishCartMutation = () => {
    pendingCartMutations.value = Math.max(0, pendingCartMutations.value - 1);
  };

  const setCartAdjustedForStock = (isAdjusted: boolean) => {
    cartAdjustedForStock.value = isAdjusted;
    if (!isAdjusted) cartStockRemovals.value = [];
  };

  const addCartStockRemovals = (removals: CartStockRemoval[]) => {
    cartAdjustedForStock.value = true;
    removals.forEach(({ title, quantity, unavailableComponentTitles = [] }) => {
      if (!title) return;
      const existingRemoval = cartStockRemovals.value.find((removal) => removal.title === title);
      if (!existingRemoval) {
        cartStockRemovals.value.push({ title, quantity, unavailableComponentTitles });
        return;
      }
      existingRemoval.quantity += quantity;
      existingRemoval.unavailableComponentTitles = [
        ...new Set([
          ...(existingRemoval.unavailableComponentTitles ?? []),
          ...unavailableComponentTitles,
        ]),
      ];
    });
  };

  const setCartAddLimitedByStock = (isLimited: boolean) => {
    cartAddLimitedByStock.value = isLimited;
  };

  const startCartAdd = () => {
    cartAddLimitedByStock.value = false;
    cartAdjustedBeforeLatestAdd.value = cartAdjustedForStock.value;
  };

  const setLatestAddLineIdentities = (lineIdentities: string[]) => {
    latestAddLineIdentities.value = lineIdentities;
  };

  const setCartUrlNotice = (notice: CartUrlNotice | null) => {
    cartUrlNotice.value = notice;
  };

  const setCartNoticesVisible = (isVisible: boolean) => {
    cartNoticesVisible.value = isVisible;
  };

  const setCart = (newCart: ShopifyCart): ShopifyCart => {
    cartData.value = newCart;
    if (!cartDataLoaded.value) cartDataLoaded.value = true;
    return cartData.value;
  };

  const cartArray = computed(() => {
    return items.value.map((item) => {
      const customBundleDetails = item.properties?._custom_bundle_details;
      let bundleSkus: string[] = [];
      if (customBundleDetails) {
        try {
          bundleSkus = JSON.parse(customBundleDetails).map((b: { sku: string }) => b.sku);
        } catch {
          console.warn('cart: failed to parse _custom_bundle_details', {
            sku: item.sku,
            key: item.key,
            customBundleDetails,
          });
        }
      }
      return {
        sku: item.sku,
        handle: item.handle,
        product_id: item.product_id?.toString(),
        variant_id: item.variant_id?.toString(),
        quantity: item.quantity,
        bundleSkus,
        isFreeGift: isFreeGiftItem(item),
      };
    });
  });

  const skuQuantities = computed(() => {
    const map = new Map<string, number>();
    cartArray.value.forEach((item) => {
      if (item.sku) {
        map.set(item.sku, (map.get(item.sku) || 0) + item.quantity);
      }
      item.bundleSkus.forEach((sku) => {
        map.set(sku, (map.get(sku) || 0) + item.quantity);
      });
    });
    return map;
  });

  const allSkusInCart = computed(() => {
    return new Set(cartArray.value.flatMap((item) => [item.sku, ...item.bundleSkus]));
  });
  const freeGiftItem = computed(
    () => items.value.find((item) => isFreeGiftItem(item) && !isSecondFreeGiftItem(item)) ?? null,
  );

  const paidNonGiftItems = computed(() =>
    items.value.filter((item) => !isFreeGiftItem(item) && item.line_price > 0),
  );

  const cartHasPaidNonGiftItems = computed(() => paidNonGiftItems.value.length > 0);

  const cartHasSku = (sku: string | string[]) => {
    const targets = Array.isArray(sku) ? sku : [sku];
    return targets.some((s) => (skuQuantities.value.get(s) || 0) > 0);
  };

  const cartHasSkuQuantity = (sku: string, requiredQuantity: number) => {
    return (skuQuantities.value.get(sku) || 0) >= requiredQuantity;
  };

  const getSkuQuantity = (sku: string) => {
    return skuQuantities.value.get(sku) || 0;
  };

  return {
    cartData,
    cartDataLoaded,
    cartUpdateInProgress,
    error,
    itemCount,
    totalPrice,
    items,
    isEmpty,
    subtotal,
    totalDiscount,
    MAX_QUANTITY,
    resolvedFreeGiftProduct,
    freeGiftOptions,
    freeGiftItem,
    paidNonGiftItems,
    cartHasPaidNonGiftItems,
    isFreeGiftSku,
    eligibleFreeGiftSkus,
    isFreeGiftEligibleSku,
    getFreeGiftProductButton,
    isFreeGiftItem,
    isSecondFreeGiftItem,
    secondFreeGiftOption,
    getFreeGiftOption,
    chosenFreeGiftSku,
    setChosenFreeGiftSku,
    setCart,
    startCartMutation,
    finishCartMutation,
    cartAdjustedForStock,
    setCartAdjustedForStock,
    cartStockRemovals,
    addCartStockRemovals,
    cartAddLimitedByStock,
    setCartAddLimitedByStock,
    cartAdjustedBeforeLatestAdd,
    startCartAdd,
    latestAddLineIdentities,
    setLatestAddLineIdentities,
    cartUrlNotice,
    setCartUrlNotice,
    cartNoticesVisible,
    setCartNoticesVisible,
    cartArray,
    allSkusInCart,
    cartHasSku,
    cartHasSkuQuantity,
    getSkuQuantity,
  };
});
